/**
 * COMPONENT: BedRecapStep
 * Étape ⑥ — récapitulatif, signatures, certificat médical, paiement.
 *
 * Cahier L100-108 :
 *   total = montants obligatoires + options choisies − réduction − acompte
 *   déjà versé. Le SOLDE VIENT TOUJOURS DU SERVEUR (`bed_solde_cents` via
 *   `get_bed_bootstrap`) : rien n'est recalculé ici, on ne fait qu'ajouter les
 *   options choisies, exactement comme le fait le socle 00166 à la soumission.
 *
 *   « L'équipage peut modifier le montant pour payer en plusieurs fois : s'il
 *   modifie le montant, il peut le diminuer mais ne jamais mettre plus que le
 *   montant global restant à payer. » → le champ est borné à ]0 ; total].
 *
 * Cahier L119-121 : signature tracée par participant (obligatoire) et
 * certificat médical joignable SANS être bloquant.
 */

import { FileCheck2, Loader2, Paperclip, X } from 'lucide-react';
import { useRef, useState } from 'react';
import type {
  BedCertificate,
  BedPricingItem,
  BedResumePaymentState,
  BedSignature,
} from '../../lib/bed/draft';
import { uploadBedCertificate } from '../../lib/bed/draft';
import { BedAmountsTable } from './BedAmountsTable';
import {
  computeLockedAmountView,
  computeOpenAmountView,
  deferralKey,
  formatCents,
  formatDueDateFr,
  toCumulativePromise,
  type BedDeferralMap,
} from '../../lib/bed/wizard-model';
import { Button } from '../ui';
import { SignaturePad } from './SignaturePad';

export type BedParticipantRole = 'pilot' | 'copilot';

export interface BedRecapAmounts {
  /** Prix total des montants obligatoires (grille serveur), en centimes. */
  prixTotalCents: number;
  /** Acompte BPI déjà réglé, en centimes. */
  acompteCents: number;
  /** Réduction équipage, en centimes. */
  reductionCents: number;
  /**
   * 00269 : nom de la réduction. Desertours règle au cas par cas un versement
   * en trop (acompte payé deux fois…) par une réduction nommée (23/09).
   * Absent : « Réduction » seul.
   */
  reductionLabel?: string | null;
  /** Solde serveur = prix total − acompte − réduction. */
  soldeCents: number;
  /**
   * Retour 35 (recette 4L du 22/09) : paiements déjà encaissés sur la fiche
   * équipage, hors acompte de pré-inscription. 0 quand le serveur n'en connaît
   * aucun — on n'affiche alors pas la ligne plutôt que d'annoncer « 0 € ».
   */
  paiementsPartielsCents: number;
}

interface BedRecapStepProps {
  token: string;
  amounts: BedRecapAmounts | null;
  /** Retour 24 : postes obligatoires de la grille, détaillés au récapitulatif. */
  mandatoryItems: BedPricingItem[];
  selectedOptions: BedPricingItem[];
  totalCents: number;
  amountToPayCents: number | null;
  onAmountToPayChange: (cents: number) => void;
  /**
   * Migration 00249 — un premier règlement a déjà été encaissé : le montant
   * choisi est figé (le tunnel en soustrait le déjà-encaissé, 00240). Le champ
   * passe en lecture seule au lieu de laisser croire au participant qu'il peut
   * encore changer d'avis.
   */
  amountLocked?: boolean;
  /**
   * État de reprise du serveur (00240). Seule source qui sache ce que le tunnel
   * encaissera quand le montant est figé : le RELIQUAT de la promesse
   * (`amount_to_pay_cents`), le déjà-encaissé et le restant dû. `null` = on ne
   * sait pas, et on n'affirme alors aucun chiffre de reste.
   */
  resumeState?: BedResumePaymentState | null;

  /** Rôles réellement présents sur ce bulletin (copilote parfois absent). */
  roles: BedParticipantRole[];
  roleLabels: Record<BedParticipantRole, string>;
  /** Retour 37 : nom et prénom du signataire, sous sa signature. Vide = non saisi. */
  roleNames: Partial<Record<BedParticipantRole, string>>;
  signatures: Partial<Record<BedParticipantRole, BedSignature>>;
  onSignatureChange: (role: BedParticipantRole, dataUrl: string | null) => void;

  certificates: Partial<Record<BedParticipantRole, BedCertificate>>;
  onCertificateChange: (role: BedParticipantRole, certificate: BedCertificate | null) => void;

  deferred: BedDeferralMap;
  onToggleCertificateDeferral: (role: BedParticipantRole, deferred: boolean) => void;
  defaultDueDate: string;
  /** 00252 (retour 21) : date butoir de paiement du solde (null = mention générique). */
  balanceDeadline?: string | null;
  /**
   * 00296 : solde couvert sans dossier. `auto` = le serveur validera la partie
   * paiement à l'envoi (free_finalization_allowed) ; `staff` = solde couvert
   * mais le service administratif finalise (preuve portée par un membre…).
   */
  nothingDue?: 'auto' | 'staff' | null;

  disabled?: boolean;
  /** Afficher les erreurs de validation (après tentative d'envoi). */
  showErrors?: boolean;
}

function CertificateUpload({
  token,
  role,
  label,
  certificate,
  isDeferred,
  defaultDueDate,
  onChange,
  onToggleDeferral,
  disabled,
  showErrors,
}: {
  token: string;
  role: BedParticipantRole;
  label: string;
  certificate: BedCertificate | undefined;
  isDeferred: boolean;
  defaultDueDate: string;
  onChange: (role: BedParticipantRole, certificate: BedCertificate | null) => void;
  onToggleDeferral: (role: BedParticipantRole, deferred: boolean) => void;
  disabled?: boolean;
  showErrors?: boolean;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleFile = async (file: File | undefined) => {
    if (!file) return;
    setBusy(true);
    setError(null);
    const result = await uploadBedCertificate(token, role, file);
    setBusy(false);

    if (result.status === 'ok') {
      onChange(role, result.certificate);
      return;
    }
    // Jamais bloquant (cahier L120) : on informe, on ne verrouille rien.
    setError(
      result.status === 'too_large'
        ? 'Fichier trop volumineux (8 Mo maximum).'
        : result.status === 'bad_type'
          ? 'Format accepté : PDF, JPEG ou PNG.'
          : 'L’envoi a échoué. Vous pourrez le transmettre plus tard.'
    );
  };

  return (
    <div className="space-y-2 rounded-lg border border-zinc-200 p-4">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <span className="text-sm font-medium text-zinc-900">Certificat médical — {label}</span>
      </div>

      {certificate ? (
        <div className="flex items-center justify-between gap-2 rounded-md bg-emerald-50 px-3 py-2">
          <span className="flex min-w-0 items-center gap-2 text-sm text-emerald-800">
            <FileCheck2 className="h-4 w-4 shrink-0" />
            <span className="truncate">{certificate.name}</span>
          </span>
          <button
            type="button"
            onClick={() => onChange(role, null)}
            disabled={disabled}
            aria-label={`Retirer le certificat médical du ${label.toLowerCase()}`}
            className="shrink-0 text-emerald-700 hover:text-emerald-900"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      ) : (
        <>
          <input
            ref={inputRef}
            type="file"
            accept="application/pdf,image/jpeg,image/png"
            className="hidden"
            onChange={(event) => handleFile(event.target.files?.[0])}
          />
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={disabled || busy}
            onClick={() => inputRef.current?.click()}
          >
            {busy ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Paperclip className="h-4 w-4" />
            )}
            Joindre un fichier
          </Button>
        </>
      )}

      {error && <p className="text-sm text-amber-700">{error}</p>}

      {!certificate && (
        <div className="flex items-start gap-2">
          <input
            type="checkbox"
            id={`defer-certificat-${role}`}
            checked={isDeferred}
            disabled={disabled}
            onChange={(event) => onToggleDeferral(role, event.target.checked)}
            className="mt-0.5 h-4 w-4 shrink-0 rounded border-zinc-300 text-amber-600 focus:ring-amber-500"
          />
          <label
            htmlFor={`defer-certificat-${role}`}
            className="cursor-pointer text-sm text-zinc-700"
          >
            Je n’ai pas cette info à disposition. Je m’engage à la communiquer
            {formatDueDateFr(defaultDueDate) && <> avant le {formatDueDateFr(defaultDueDate)}</>}.
            <span className="ml-1 text-red-500">*</span>
          </label>
        </div>
      )}
      {showErrors && !certificate && !isDeferred && (
        <p className="text-sm text-red-600">
          Veuillez joindre votre certificat médical ou cocher la case ci-dessus.
        </p>
      )}
    </div>
  );
}

export function BedRecapStep({
  token,
  mandatoryItems,
  amounts,
  selectedOptions,
  totalCents,
  amountToPayCents,
  onAmountToPayChange,
  amountLocked = false,
  resumeState = null,
  roles,
  roleLabels,
  roleNames,
  signatures,
  onSignatureChange,
  certificates,
  onCertificateChange,
  deferred,
  onToggleCertificateDeferral,
  defaultDueDate,
  balanceDeadline = null,
  nothingDue = null,
  disabled = false,
  showErrors = false,
}: BedRecapStepProps) {
  // Montant FIGÉ (00249) : ce que le tunnel encaissera réellement, jamais la
  // promesse brute. `null` = le serveur n'a pas dit le restant dû → on affiche
  // le montant enregistré sans rien affirmer de plus.
  const lockedView = amountLocked ? computeLockedAmountView(resumeState) : null;
  // Montant OUVERT (revue du 23/09, point 6) : premier envoi alors que des
  // versements ont déjà été encaissés. Le participant raisonne sur ce qui lui
  // reste ; `amountToPayCents` est la promesse CUMULÉE que le tunnel lira
  // (00240) — la saisie y est convertie, jamais persistée telle quelle.
  const paidBeforeCents = amountLocked ? 0 : (amounts?.paiementsPartielsCents ?? 0);
  const openView = computeOpenAmountView({
    totalCents,
    paidCents: paidBeforeCents,
    promiseCents: amountToPayCents,
  });
  const effectiveAmount = lockedView
    ? lockedView.chargeCents
    : amountLocked
      ? (amountToPayCents ?? totalCents)
      : openView.chargeCents;
  // Borne haute affichée : quand un paiement a commencé, c'est le restant dû du
  // dossier qui fait foi — le total du bulletin peut avoir baissé sous la
  // promesse figée (une option désactivée entre deux soumissions).
  const referenceCents = lockedView
    ? lockedView.chargeCents + lockedView.remainderCents
    : openView.referenceCents;
  // La borne du champ ne passe jamais SOUS la valeur affichée : un montant figé
  // peut dépasser un total qui a baissé, et un `max` plus petit rendrait le
  // champ invalide sans que le participant puisse rien y faire.
  const maxCents = amountLocked ? Math.max(referenceCents, effectiveAmount) : referenceCents;
  const isPartial = !amountLocked && openView.remainderCents > 0;
  // Les versements couvrent déjà tout : il n'y a rien à saisir.
  const alreadyCovered = !amountLocked && paidBeforeCents > 0 && openView.referenceCents === 0;

  return (
    <div className="space-y-8">
      {/* ---------------- Chiffrage ---------------- */}
      <section className="space-y-3">
        <h3 className="text-base font-semibold text-zinc-900">Votre récapitulatif</h3>

        <BedAmountsTable
          amounts={amounts}
          mandatoryItems={mandatoryItems}
          selectedOptions={selectedOptions}
          totalCents={totalCents}
          variant="frozen"
          balanceDeadline={balanceDeadline}
          resumeState={resumeState}
          testId="bed-recap"
        />
      </section>

      {/* Recette du 07/10 (Floran) : rien à payer, le récapitulatif le dit et
          l'envoi du bulletin vaut validation (00296, sans dossier). */}
      {nothingDue && (
        <p
          className="rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-800"
          data-testid="bed-nothing-due"
        >
          {nothingDue === 'auto'
            ? 'Rien à payer : la partie paiement sera validée à l’envoi de votre bulletin.'
            : 'Votre solde est déjà couvert : après l’envoi de votre bulletin, notre service administratif finalisera votre dossier.'}
          {totalCents < 0 &&
            ` Votre acompte et votre réduction dépassent le total de ${formatCents(-totalCents)}.`}
        </p>
      )}

      {/* ---------------- Montant réglé maintenant ---------------- */}
      {alreadyCovered && (
        <p
          className="rounded-md bg-zinc-50 px-3 py-2 text-sm text-zinc-700"
          data-testid="bed-amount-covered"
        >
          Vos versements couvrent déjà le total : aucun paiement supplémentaire ne vous sera
          demandé.
        </p>
      )}
      {amounts !== null && totalCents > 0 && !alreadyCovered && (
        <section className="space-y-2">
          <label htmlFor="bed-amount-to-pay" className="block text-sm font-medium text-zinc-900">
            Montant que vous réglez maintenant
          </label>
          <div className="flex flex-wrap items-center gap-2">
            {/*
              Verrou : `readOnly` SEUL, jamais `disabled` (revue adversariale
              09/2026) — `disabled` sortait le champ du parcours clavier sans
              jamais en énoncer la raison. `aria-describedby` pointe l'explication.
            */}
            <input
              id="bed-amount-to-pay"
              type="number"
              min={1}
              max={Math.floor(maxCents / 100)}
              step="1"
              value={Math.round(effectiveAmount / 100)}
              disabled={disabled}
              readOnly={amountLocked}
              aria-describedby="bed-amount-note"
              onChange={(event) => {
                if (amountLocked) return;
                onAmountToPayChange(
                  toCumulativePromise(
                    Number(event.target.value) * 100,
                    paidBeforeCents,
                    openView.referenceCents
                  )
                );
              }}
              className="h-10 w-40 rounded-md border border-zinc-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-zinc-900 disabled:bg-zinc-50 disabled:text-zinc-500 read-only:bg-zinc-50 read-only:text-zinc-600"
            />
            {/*
              Ce à quoi le montant se compare. Verrouillé, c'est le restant dû
              du DOSSIER : le total du bulletin peut avoir baissé sous la
              promesse figée (option désactivée entre deux soumissions), et
              « 2 000 € sur 1 500 € » serait faux. Restant dû inconnu : on ne
              compare à rien plutôt que de comparer à tort.
            */}
            <span className="text-sm text-zinc-600">
              {lockedView
                ? `€ sur ${formatCents(referenceCents)} restant dû`
                : amountLocked
                  ? '€'
                  : paidBeforeCents > 0
                    ? `€ sur ${formatCents(referenceCents)} restant dû`
                    : `€ sur ${formatCents(totalCents)}`}
            </span>
          </div>
          {amountLocked ? (
            // 00249 + revue adversariale : le montant persisté est la PROMESSE,
            // dont 00240 soustrait le déjà-encaissé. On annonce donc ce que le
            // tunnel prélèvera vraiment, pas la promesse brute — et rien du tout
            // sur le reste quand le serveur ne nous a pas donné le restant dû.
            <p
              className="text-xs text-zinc-600"
              id="bed-amount-note"
              data-testid="bed-amount-locked"
            >
              {lockedView ? (
                <>
                  {lockedView.paidCents !== null && lockedView.paidCents > 0
                    ? `Un premier règlement de ${formatCents(lockedView.paidCents)} a déjà été encaissé : ce montant ne peut plus être modifié.`
                    : 'Un premier règlement a déjà été encaissé : ce montant ne peut plus être modifié.'}{' '}
                  {lockedView.remainderCents > 0
                    ? `Il vous restera ${formatCents(lockedView.remainderCents)} à régler après ce paiement.`
                    : 'Ce paiement soldera votre dossier.'}
                </>
              ) : resumeState?.settled || resumeState?.nothing_due ? (
                // Dossier déjà soldé (revue adversariale 09/2026) : le serveur
                // ne renvoie plus de restant dû. Annoncer un prélèvement « à
                // confirmer » serait faux — il n'y aura aucun paiement.
                <>
                  Votre solde est déjà réglé : ce montant ne peut plus être modifié et aucun
                  paiement supplémentaire ne vous sera demandé.
                </>
              ) : (
                <>
                  Un premier règlement a déjà été encaissé : ce montant ne peut plus être modifié.
                  Le montant réellement prélevé (ce choix moins ce qui est déjà encaissé) vous sera
                  confirmé à l’ouverture du paiement.
                </>
              )}
            </p>
          ) : (
            <p className="text-xs text-zinc-500" id="bed-amount-note">
              {paidBeforeCents > 0 &&
                `Vous avez déjà versé ${formatCents(paidBeforeCents)} : ce montant s’y ajoute. `}
              Vous pouvez régler en plusieurs fois : diminuez ce montant si vous le souhaitez. Il ne
              peut jamais dépasser le total restant dû.
            </p>
          )}
          {isPartial && (
            <p
              className="rounded-md bg-zinc-50 px-3 py-2 text-sm text-zinc-700"
              data-testid="bed-partial-notice"
            >
              Il vous restera {formatCents(openView.remainderCents)} à régler après ce paiement.
            </p>
          )}
        </section>
      )}

      {/* ---------------- Certificats médicaux ---------------- */}
      <section className="space-y-3">
        <h3 className="text-base font-semibold text-zinc-900">Certificat médical</h3>
        <div className="space-y-3">
          {roles.map((role) => (
            <CertificateUpload
              key={role}
              token={token}
              role={role}
              label={roleLabels[role]}
              certificate={certificates[role]}
              isDeferred={Boolean(deferred[deferralKey('certificat', role)])}
              defaultDueDate={defaultDueDate}
              onChange={onCertificateChange}
              onToggleDeferral={onToggleCertificateDeferral}
              disabled={disabled}
              showErrors={showErrors}
            />
          ))}
        </div>
      </section>

      {/* ---------------- Signatures ---------------- */}
      <section className="space-y-3">
        <h3 className="text-base font-semibold text-zinc-900">Signatures</h3>
        <p className="text-sm text-zinc-600">
          Chaque participant signe son bulletin. La signature est horodatée et conservée avec votre
          dossier.
        </p>
        <div className="space-y-5">
          {roles.map((role) => (
            <SignaturePad
              key={role}
              label={
                roleNames[role]
                  ? `Signature — ${roleLabels[role]} : ${roleNames[role]}`
                  : `Signature — ${roleLabels[role]}`
              }
              value={signatures[role]?.data_url ?? null}
              signedAt={signatures[role]?.signed_at ?? null}
              disabled={disabled}
              onChange={(dataUrl) => onSignatureChange(role, dataUrl)}
            />
          ))}
        </div>
      </section>
    </div>
  );
}
